import type { HealthApp, HealthUptimeDay, HealthUptimeHistory } from '@traceio-org/platform-contracts';

/**
 * The uptime grid's arithmetic: one square per UTC day and app from the sampled history (platform-api's
 * health-sample job). A day's uptime is (healthy + degraded) / samples: a degraded app still serves, so it counts as up,
 * and the square is coloured "slow" instead. A day without samples has no data (grey), never 100%.
 */

export const UPTIME_PERIODS = [30, 90] as const;
export type UptimePeriod = (typeof UPTIME_PERIODS)[number];

/** The colour buckets, best first; each has a word in the legend and the tooltip, colour is never the only signal. */
export const UPTIME_BUCKETS = ['full', 'high', 'ok', 'slow', 'down', 'none'] as const;
export type UptimeBucket = (typeof UPTIME_BUCKETS)[number];

export const BUCKET_LABELS: Readonly<Record<UptimeBucket, string>> = {
  full: '100%',
  high: '99.9%+',
  ok: '99%+',
  slow: 'Slow',
  down: 'Down',
  none: 'No data',
};

/** Below this a period's figure is shown amber and bold. */
export const UPTIME_TARGET_PERCENT = 99.9;

export interface UptimeCell {
  day: string;
  bucket: UptimeBucket;
  /** Uptime that day, percent, or null without samples. */
  percent: number | null;
  downMinutes: number;
  slowMinutes: number;
  reason: string | null;
  today: boolean;
}

export interface UptimeRow {
  appId: string;
  label: string;
  cells: UptimeCell[];
  /** Uptime over the period's sampled days, percent, or null without any sample. */
  percent: number | null;
}

const DAY_MS = 86_400_000;

/** The UTC days of the period, oldest first, ending at `to` (inclusive). */
export function periodDays(to: string, days: number): string[] {
  const end = Date.parse(`${to}T00:00:00Z`);
  return Array.from({ length: days }, (_, i) =>
    new Date(end - (days - 1 - i) * DAY_MS).toISOString().slice(0, 10),
  );
}

export function dayPercent(day: Pick<HealthUptimeDay, 'samples' | 'healthy' | 'degraded'>): number | null {
  return day.samples > 0 ? ((day.healthy + day.degraded) / day.samples) * 100 : null;
}

/**
 * A day's colour: no samples is no data; under 99% up is down; any degraded sample (still up) is slow; then 100%,
 * 99.9% and over, 99% and over.
 */
export function bucketOf(day: HealthUptimeDay | undefined): UptimeBucket {
  if (!day || day.samples === 0) return 'none';
  const percent = dayPercent(day)!;
  if (percent < 99) return 'down';
  if (day.degraded > 0) return 'slow';
  if (percent === 100) return 'full';
  if (percent >= 99.9) return 'high';
  return 'ok';
}

function periodPercent(days: (HealthUptimeDay | undefined)[]): number | null {
  let up = 0;
  let samples = 0;
  for (const day of days) {
    if (!day) continue;
    up += day.healthy + day.degraded;
    samples += day.samples;
  }
  return samples > 0 ? (up / samples) * 100 : null;
}

/**
 * One row per current app (by its display name), in name order, with one cell per day of the period. History of an app
 * no longer in the environment is left out; an app without history has only grey days.
 */
export function uptimeRows(
  history: HealthUptimeHistory,
  apps: HealthApp[],
  period: UptimePeriod,
): UptimeRow[] {
  const days = periodDays(history.to, period);
  const byApp = new Map(history.apps.map((app) => [app.appId, new Map(app.days.map((d) => [d.day, d]))]));
  return [...apps]
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map((app) => {
      const sampled = byApp.get(app.id);
      const dayRows = days.map((day) => sampled?.get(day));
      return {
        appId: app.id,
        label: app.displayName,
        percent: periodPercent(dayRows),
        cells: days.map((day, index) => {
          const row = dayRows[index];
          return {
            day,
            bucket: bucketOf(row),
            percent: row ? dayPercent(row) : null,
            downMinutes: row?.downMinutes ?? 0,
            slowMinutes: row ? row.degraded * history.intervalMinutes : 0,
            reason: row?.reason ?? null,
            today: day === history.to,
          };
        }),
      };
    });
}

/** Uptime across every app of the period, percent, or null without any sample (the "Uptime" metric). */
export function overallPercent(history: HealthUptimeHistory, period: UptimePeriod): number | null {
  const days = new Set(periodDays(history.to, period));
  return periodPercent(history.apps.flatMap((app) => app.days.filter((d) => days.has(d.day))));
}

/** The month labels above the grid: the column of each first of a month and its short name. */
export function monthLabels(days: string[]): { column: number; label: string }[] {
  const format = new Intl.DateTimeFormat('en', { month: 'short', timeZone: 'UTC' });
  return days.flatMap((day, index) =>
    day.endsWith('-01') ? [{ column: index, label: format.format(new Date(`${day}T00:00:00Z`)) }] : [],
  );
}

const shortDay = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', timeZone: 'UTC' });

export function formatDay(day: string): string {
  return shortDay.format(new Date(`${day}T00:00:00Z`));
}

/** The tooltip's second line: "97.6% · down 34 min" (only the parts with data). */
export function cellSummary(cell: UptimeCell): string {
  if (cell.percent === null) return 'No data';
  const parts = [formatUptime(cell.percent)];
  if (cell.downMinutes > 0) parts.push(`down ${cell.downMinutes} min`);
  if (cell.slowMinutes > 0) parts.push(`slow ${cell.slowMinutes} min`);
  return parts.join(' · ');
}

/** An uptime figure: 100% exactly, else one decimal (two near the target, so 99.95% is not shown as 100%). */
export function formatUptime(percent: number): string {
  if (percent === 100) return '100%';
  const digits = percent >= 99 ? 2 : 1;
  const factor = 10 ** digits;
  return `${Math.floor(percent * factor) / factor}%`;
}
